import { useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResultSetup, type ResultContext } from "./ResultSetup";
import { TemplateGenerator } from "./TemplateGenerator";
import { BulkUpload, type ParsedResult } from "./BulkUpload";
import { ValidationPreview } from "./ValidationPreview";
import { IndividualEntry } from "./IndividualEntry";
import { ApprovalWorkflow } from "./ApprovalWorkflow";
import { ModulesManager } from "./ModulesManager";
import { ResultVisibilityManager } from "./ResultVisibilityManager";
import { StudentResultVisibilityBrowser } from "./StudentResultVisibilityBrowser";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useModuleVisibility } from "@/hooks/useModuleVisibility";
import { canManageModule } from "@/lib/permissions";
import { FileSpreadsheet, Upload, UserPlus, ClipboardList, BookOpen, Shield } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const normalizeCode = (s: string) => s.replace(/\s+/g, '').toUpperCase();

function chunkArray<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
}

function deriveRemark(grade: string | null): string | null {
  if (!grade) return null;
  const g = grade.toUpperCase().replace(/[+-]/g, "");
  if (["A", "B", "C"].includes(g)) return "PASS";
  if (g === "D") return "SUPP";
  if (g === "F") return "FAIL";
  return null;
}

export function ResultsWorkflow() {
  const { toast } = useToast();
  const { user, roles } = useAuth();
  const { isModuleVisible } = useModuleVisibility(roles);
  const canManageExamination = canManageModule(roles, "examination");
  const canSeeVisibility = isModuleVisible("result_visibility");

  const queryClient = useQueryClient();
  const [context, setContext] = useState<ResultContext>({
    semester: "",
    selectedClass: "",
    examType: "",
    academicYear: "",
    studentSource: "signed_up",
    mode: "new",
  });
  const [parsed, setParsed] = useState<ParsedResult | null>(null);
  const [activeTab, setActiveTab] = useState("bulk");
  const [reuploadReport, setReuploadReport] = useState<null | {
    imported: number;
    overallChanged: number;
    gpaSynced: number;
    batchCount: number;
    skippedDetails: Array<{ student: string; module: string; reason: string }>;
  }>(null);

  useEffect(() => {
    if (!canManageExamination && activeTab !== "approval" && !(canSeeVisibility && activeTab === "visibility")) {
      setActiveTab("approval");
    }
  }, [activeTab, canManageExamination, canSeeVisibility]);

  const isPre = context.studentSource === "pre_registered";
  const visibleTabCount = (canManageExamination ? 5 : 1) + (canSeeVisibility ? 1 : 0);
  const tabsLayoutClassName = {
    1: "max-w-xs grid-cols-1",
    2: "max-w-md grid-cols-2",
    5: "max-w-3xl grid-cols-5",
    6: "max-w-3xl grid-cols-6",
  }[visibleTabCount];

  const saveBatchMutation = useMutation({
    mutationFn: async () => {
      if (!parsed || !user) throw new Error("No parsed data");
      const validRows = parsed.rows.filter((r) => r.errors.length === 0);
      if (validRows.length === 0) throw new Error("No valid rows to import");

      // ============== RE-UPLOAD (Supplementary / Repeat / Incomplete) FLOW ==============
      if (context.mode === "reupload") {
        const { isFailingResult } = await import("@/lib/examStatus");

        // Find ALL matching batches (a class can have multiple published batches for the same key)
        const { data: batches, error: batchErr } = await supabase
          .from("exam_batches")
          .select("id, status")
          .eq("class", context.selectedClass)
          .eq("semester", context.semester)
          .eq("exam_type", context.examType)
          .eq("academic_year", context.academicYear)
          .in("status", ["pending_approval", "approved", "verified", "published"])
          .order("created_at", { ascending: false });
        if (batchErr) throw batchErr;
        if (!batches || batches.length === 0) {
          throw new Error(
            "No existing approved/published batch found for this Class / Semester / Exam Type / Year. Use 'New Batch' mode for the first upload."
          );
        }
        const batchIds = batches.map((b) => b.id);
        const originalBatchId = batches[0].id;

        // Load existing failing results across ALL matching batches.
        // Push the failing-rows filter to the server and paginate to avoid the
        // 1000-row default cap silently truncating SUPP students for larger classes.
        const pageSize = 1000;
        const failingFilter =
          "remark.in.(FAIL,SUPP,INC,INCOMPLETE,REPEAT)," +
          "overall_remark.in.(FAIL,SUPP,INC,INCOMPLETE,REPEAT)," +
          "grade.in.(F,D,D+,D-,E,I,INC,SUPP)";
        const existingResults: Array<any> = [];
        for (let from = 0; ; from += pageSize) {
          const { data: page, error: exErr } = await supabase
            .from("exam_results")
            .select("id, batch_id, student_id, form_iv_index, exam_type, marks, grade, remark, overall_remark")
            .in("batch_id", batchIds)
            .or(failingFilter)
            .range(from, from + pageSize - 1);
          if (exErr) throw exErr;
          if (!page || page.length === 0) break;
          existingResults.push(...page);
          if (page.length < pageSize) break;
        }

        // Index existing rows by `${form_iv_index||student_id}|${moduleCode}`
        const keyOf = (sidOrFiv: string, moduleCode: string) =>
          `${(sidOrFiv || "").trim().toUpperCase()}|${normalizeCode(moduleCode)}`;
        const existingMap = new Map<string, any>();
        for (const r of existingResults ?? []) {
          const examParts = (r.exam_type || "").split(" - ");
          const code = examParts.length > 1 ? examParts.slice(1).join(" - ") : (r.exam_type || "");
          const fiv = (r.form_iv_index || "").trim().toUpperCase();
          if (fiv) existingMap.set(keyOf(fiv, code), r);
          if (r.student_id) existingMap.set(keyOf(r.student_id, code), r);
        }

        const updates: Array<{ id: string; batch_id: string; marks: number | null; grade: string | null; remark: string | null; }> = [];
        const attempts: Array<any> = [];
        const skipped: Array<{ student: string; module: string; reason: string }> = [];

        // Index uploaded per-student values (GPA + explicit Overall Remark)
        // so we can sync them onto ALL of the student's result rows, not just
        // the supp module being re-scored.
        const uploadedByStudent = new Map<string, { gpa: string; remarks: string }>();
        for (const r of validRows) {
          const key = (r.formIvIndex || r.studentId || "").trim().toUpperCase();
          if (key) uploadedByStudent.set(key, { gpa: r.gpa || "", remarks: r.remarks || "" });
        }

        // Track which uploaded rows actually matched at least one existing record
        // — anything left over is a "student not found in any failing batch".
        const matchedUploadKeys = new Set<string>();

        for (const r of validRows) {
          for (const code of parsed.moduleCodes) {
            const val = r.scores[code];
            if (val === undefined || val === null || val === "") continue;

            const lookup =
              existingMap.get(keyOf(r.formIvIndex || r.studentId, code)) ||
              existingMap.get(keyOf(r.studentId, code));

            if (!lookup) {
              skipped.push({ student: r.studentId, module: code, reason: "no prior result" });
              continue;
            }
            if (!isFailingResult(lookup)) {
              skipped.push({ student: r.studentId, module: code, reason: "already passed" });
              continue;
            }

            const isNumeric = typeof val === "number";
            const newGrade = isNumeric ? null : String(val).toUpperCase();
            const newMarks = isNumeric ? (val as number) : null;
            const newRemark = deriveRemark(newGrade) ?? lookup.remark ?? null;

            updates.push({
              id: lookup.id,
              batch_id: lookup.batch_id,
              marks: newMarks,
              grade: newGrade,
              remark: newRemark,
            });
            attempts.push({
              result_id: lookup.id,
              attempt_number: 2,
              previous_marks: lookup.marks,
              previous_grade: lookup.grade,
              previous_remark: lookup.remark,
              new_marks: newMarks,
              new_grade: newGrade,
              new_remark: newRemark,
              exam_type: context.examType,
              uploaded_by: user.id,
            });
          }
        }

        // Apply updates one by one (small volumes typical for supp uploads)
        for (const u of updates) {
          const { error: upErr } = await supabase
            .from("exam_results")
            .update({
              marks: u.marks,
              grade: u.grade,
              remark: u.remark,
              status: "pending",
              updated_at: new Date().toISOString(),
            })
            .eq("id", u.id);
          if (upErr) throw upErr;
        }

        // Insert attempt history (best-effort; don't fail the whole upload)
        if (attempts.length > 0) {
          const { error: attErr } = await (supabase as any)
            .from("exam_result_attempts")
            .insert(attempts);
          if (attErr) console.warn("Attempt history insert failed:", attErr);
        }

        // Recompute overall_remark + sync GPA for ALL students in the upload
        // (not just those whose module scores changed). This ensures that when
        // every module is now passing, the student's overall flips to PASS even
        // if no score needed updating, AND that the manually-entered GPA on the
        // re-upload file replaces the stale GPA from the original batch.
        const studentBatchPairs = new Set<string>();
        const pairToFiv = new Map<string, string>(); // composite → form_iv (UPPER) for upload lookup
        // From updates (covers re-scored modules)
        for (const u of updates) {
          const lookup = (existingResults ?? []).find((r) => r.id === u.id);
          if (!lookup) continue;
          const sKey = lookup.student_id || lookup.form_iv_index;
          if (sKey) {
            const comp = `${u.batch_id}::${sKey}::${lookup.student_id ? "id" : "fiv"}`;
            studentBatchPairs.add(comp);
            const fiv = (lookup.form_iv_index || "").trim().toUpperCase();
            if (fiv) pairToFiv.set(comp, fiv);
          }
        }
        // From every valid uploaded row that matched an existing student in any batch
        for (const r of validRows) {
          const sid = (r.formIvIndex || r.studentId || "").trim().toUpperCase();
          if (!sid) continue;
          for (const ex of existingResults ?? []) {
            const fiv = (ex.form_iv_index || "").trim().toUpperCase();
            if (fiv && fiv === sid) {
              const comp = `${ex.batch_id}::${ex.form_iv_index}::fiv`;
              studentBatchPairs.add(comp);
              pairToFiv.set(comp, sid);
              matchedUploadKeys.add(sid);
            } else if (ex.student_id && ex.student_id.toUpperCase() === sid) {
              const comp = `${ex.batch_id}::${ex.student_id}::id`;
              studentBatchPairs.add(comp);
              pairToFiv.set(comp, sid);
              matchedUploadKeys.add(sid);
            }
          }
        }

        // Detect uploaded students who never matched any existing failing batch
        for (const [key] of uploadedByStudent) {
          if (!matchedUploadKeys.has(key)) {
            skipped.push({ student: key, module: "(all)", reason: "student not found in any prior failing batch" });
          }
        }

        let overallChanged = 0;
        let gpaSynced = 0;
        for (const composite of studentBatchPairs) {
          const [bId, sKey, kind] = composite.split("::");
          const col = kind === "id" ? "student_id" : "form_iv_index";
          const { data: studentRows } = await supabase
            .from("exam_results")
            .select("id, remark, grade, overall_remark, gpa")
            .eq("batch_id", bId)
            .eq(col, sKey);
          if (!studentRows || studentRows.length === 0) continue;

          // Use ONLY each module row's own remark/grade. Do not consider overall_remark
          // when judging an individual module — it is a student-level flag and would
          // incorrectly mark passed modules as failed.
          const isModulePass = (r: any) => {
            const rm = (r.remark || "").toUpperCase().trim();
            if (rm === "PASS") return true;
            if (rm && rm !== "") return false;
            const g = (r.grade || "").toUpperCase().replace(/[+-]/g, "");
            return ["A", "B", "C"].includes(g);
          };
          const isModuleFail = (r: any) => {
            const rm = (r.remark || "").toUpperCase().trim();
            if (rm === "FAIL") return true;
            const g = (r.grade || "").toUpperCase().replace(/[+-]/g, "");
            return g === "F";
          };

          const allPass = studentRows.every(isModulePass);
          const anyFail = studentRows.some(isModuleFail);
          const computedOverall = allPass ? "PASS" : anyFail ? "FAIL" : "SUPP";

          // Honor explicit Overall Remark from the uploaded file
          // (DISCONTINUE / REPEAT / FAIL / PASS / SUPP take precedence over computed)
          const fivKey = pairToFiv.get(composite);
          const upload = fivKey ? uploadedByStudent.get(fivKey) : undefined;
          const rem = (upload?.remarks || "").toUpperCase().trim();
          let explicitOverall: string | null = null;
          for (const kw of ["DISCONTINUE", "REPEAT", "FAIL", "PASS", "SUPP"]) {
            if (rem.includes(kw)) { explicitOverall = kw; break; }
          }
          const finalOverall = explicitOverall || computedOverall;

          const currentOverall = (studentRows.find((r) => r.overall_remark)?.overall_remark || "").toUpperCase();
          const newGpa = (upload?.gpa || "").trim() || null;
          const currentGpa = (studentRows.find((r) => r.gpa)?.gpa || "").toString().trim() || null;

          const overallChangedNow = finalOverall !== currentOverall;
          const gpaChangedNow = newGpa !== null && newGpa !== currentGpa;

          if (overallChangedNow || gpaChangedNow) {
            const patch: { updated_at: string; overall_remark?: string; gpa?: string } = {
              updated_at: new Date().toISOString(),
            };
            if (overallChangedNow) patch.overall_remark = finalOverall;
            if (gpaChangedNow && newGpa) patch.gpa = newGpa;
            await supabase
              .from("exam_results")
              .update(patch)
              .eq("batch_id", bId)
              .eq(col, sKey);
            if (overallChangedNow) overallChanged++;
            if (gpaChangedNow) gpaSynced++;
          }
        }

        // Reset every touched batch back to DRAFT so the Examiner re-enters the
        // normal approval flow (draft → submit → pending_approval → approved →
        // pending_verification → verified → published) instead of skipping
        // straight to pending_approval.
        const touchedBatchIds = Array.from(
          new Set([
            ...updates.map((u) => u.batch_id).filter(Boolean),
            ...Array.from(studentBatchPairs).map((c) => c.split("::")[0]),
          ])
        );
        if (touchedBatchIds.length > 0) {
          await supabase
            .from("exam_batches")
            .update({
              status: "draft",
              approved_by: null,
              approved_at: null,
              rejection_notes: null,
              updated_at: new Date().toISOString(),
            })
            .in("id", touchedBatchIds);

          // Supersede any pending verifier assignments for these batches so a
          // verifier doesn't act on the now-stale results. The Examiner will
          // resubmit and a fresh verifier will be assigned at approval time.
          await (supabase as any)
            .from("exam_batch_verifications")
            .update({ status: "superseded", updated_at: new Date().toISOString() })
            .in("batch_id", touchedBatchIds)
            .eq("status", "pending");
        }

        return {
          imported: updates.length,
          batchId: touchedBatchIds[0] ?? originalBatchId,
          batchCount: touchedBatchIds.length,
          skipped: skipped.length,
          skippedDetails: skipped,
          overallChanged,
          gpaSynced,
          reupload: true as const,
        };
      }
      // ============== END RE-UPLOAD FLOW ==============

      const { data: courses } = await supabase
        .from("courses")
        .select("id")
        .eq("class", context.selectedClass)
        .limit(1);
      const courseId = courses?.[0]?.id;
      if (!courseId) throw new Error(`No course found for class ${context.selectedClass}`);

      const { data: modulesDb } = await supabase
        .from("modules")
        .select("id, module_code")
        .eq("class", context.selectedClass);
      const moduleMap = new Map((modulesDb ?? []).map((m) => [m.module_code.trim(), m.id]));

      // Build results FIRST before touching the batch — prevents empty batches
      let results: any[];

      // Normalize identifiers (trim + uppercase) so casing/whitespace differences
      // between the template and the database don't cause "No matching students".
      const normId = (v: string | null | undefined) => String(v ?? "").trim().toUpperCase();

      if (isPre) {
        const formIvIndexes = Array.from(new Set(
          validRows.map((r) => normId(r.formIvIndex || r.studentId)).filter(Boolean)
        ));

        // Batch lookups in chunks of 50 to avoid URL length limits
        const preRegMap = new Map<string, string>();
        for (const chunk of chunkArray(formIvIndexes, 50)) {
          const { data } = await (supabase as any)
            .from("student_pre_registrations_exam_safe")
            .select("id, form_iv_index")
            .in("form_iv_index", chunk);
          (data ?? []).forEach((p: any) => { if (p.form_iv_index) preRegMap.set(normId(p.form_iv_index), p.id); });
        }

        const signedUpMap = new Map<string, string>();
        for (const chunk of chunkArray(formIvIndexes, 50)) {
          const { data } = await supabase
            .from("students")
            .select("id, form_iv_index")
            .in("form_iv_index", chunk);
          (data ?? []).forEach((s) => { if (s.form_iv_index) signedUpMap.set(normId(s.form_iv_index), s.id); });
        }

        results = validRows
          .filter((r) => preRegMap.has(normId(r.formIvIndex || r.studentId)))
          .flatMap((r) => {
            const key = normId(r.formIvIndex || r.studentId);
            const retakeSet = new Set(
              r.modulesToRetake.split(",").map(c => normalizeCode(c)).filter(Boolean)
            );
            const overallUpper = (r.remarks || "").toUpperCase();
            const isDiscontinueOrRepeat = overallUpper.includes("DISCONTINUE") || overallUpper.includes("REPEAT");
            return parsed.moduleCodes
              .filter((code) => {
                const val = r.scores[code];
                return val !== undefined && val !== null && val !== "";
              })
              .map((code) => {
                const val = r.scores[code];
                const isNumeric = typeof val === "number";
                return {
                  student_id: signedUpMap.get(key) || null,
                  pre_registration_id: preRegMap.get(key)!,
                  student_source: "pre_registered",
                  form_iv_index: r.formIvIndex || r.studentId,
                  course_id: courseId,
                  marks: isNumeric ? val : null,
                  grade: isNumeric ? null : String(val),
                  semester: context.semester,
                  academic_year: context.academicYear,
                  exam_type: `${context.examType} - ${code}`,
                  batch_id: "", // placeholder, set after batch creation
                  status: "pending",
                  remark: retakeSet.size > 0
                    ? (retakeSet.has(normalizeCode(code))
                        ? (isDiscontinueOrRepeat ? "FAIL" : "SUPP")
                        : "PASS")
                    : (r.remarks || deriveRemark(isNumeric ? null : String(val)) || null),
                  overall_remark: r.remarks || null,
                  gpa: r.gpa || null,
                };
              });
          });
      } else {
        // r.studentId may be Form IV Index (when populated in template) or student_number.
        // Look up by BOTH columns so either identifier resolves to the student.
        const studentNumbers = Array.from(new Set(validRows.map((r) => normId(r.studentId)).filter(Boolean)));
        const formIvIndexes = Array.from(new Set(
          validRows.map((r) => normId(r.formIvIndex)).filter(Boolean)
        ));

        const studentMap = new Map<string, { id: string; student_number: string; form_iv_index: string | null }>();
        const addToMap = (s: { id: string; student_number: string | null; form_iv_index: string | null }) => {
          if (s.student_number) studentMap.set(normId(s.student_number), s as any);
          if (s.form_iv_index) studentMap.set(normId(s.form_iv_index), s as any);
        };
        for (const chunk of chunkArray(studentNumbers, 50)) {
          const { data } = await supabase
            .from("students")
            .select("id, student_number, form_iv_index")
            .in("student_number", chunk);
          (data ?? []).forEach(addToMap);
        }
        for (const chunk of chunkArray(formIvIndexes, 50)) {
          const { data } = await supabase
            .from("students")
            .select("id, student_number, form_iv_index")
            .in("form_iv_index", chunk);
          (data ?? []).forEach(addToMap);
        }

        const resolveStudent = (r: typeof validRows[number]) =>
          studentMap.get(normId(r.studentId)) ?? (r.formIvIndex ? studentMap.get(normId(r.formIvIndex)) : undefined);

        results = validRows
          .filter((r) => !!resolveStudent(r))
          .flatMap((r) => {
            const stu = resolveStudent(r)!;
            const retakeSet = new Set(
              r.modulesToRetake.split(",").map(c => normalizeCode(c)).filter(Boolean)
            );
            const overallUpper = (r.remarks || "").toUpperCase();
            const isDiscontinueOrRepeat = overallUpper.includes("DISCONTINUE") || overallUpper.includes("REPEAT");
            return parsed.moduleCodes
              .filter((code) => {
                const val = r.scores[code];
                return val !== undefined && val !== null && val !== "";
              })
              .map((code) => {
                const val = r.scores[code];
                const isNumeric = typeof val === "number";
                return {
                  student_id: stu.id,
                  student_source: "signed_up",
                  form_iv_index: stu.form_iv_index || null,
                  course_id: courseId,
                  marks: isNumeric ? val : null,
                  grade: isNumeric ? null : String(val),
                  semester: context.semester,
                  academic_year: context.academicYear,
                  exam_type: `${context.examType} - ${code}`,
                  batch_id: "", // placeholder, set after batch creation
                  status: "pending",
                  remark: retakeSet.size > 0
                    ? (retakeSet.has(normalizeCode(code))
                        ? (isDiscontinueOrRepeat ? "FAIL" : "SUPP")
                        : "PASS")
                    : (r.remarks || deriveRemark(isNumeric ? null : String(val)) || null),
                  overall_remark: r.remarks || null,
                  gpa: r.gpa || null,
                };
              });
          });
      }
      if (results.length === 0) {
        const sampleIds = validRows.slice(0, 3).map((r) => r.formIvIndex || r.studentId).filter(Boolean).join(", ");
        throw new Error(
          (isPre
            ? "No matching students found in pre-registration records. "
            : "No matching students found in the system. "
          ) +
          `Checked ${validRows.length} row(s). Verify the student source is correct and that Student ID / Form IV Index values match registered students. Sample IDs from file: ${sampleIds || "(none)"}.`
        );
      }

      // Count unique matched students for accurate total_students
      const matchedStudentIds = new Set(results.map((r) => r.form_iv_index || r.student_id));
      const matchedStudentCount = matchedStudentIds.size;

      // Now that we confirmed results exist, create or reuse the batch
      const { data: existingBatch } = await supabase
        .from("exam_batches")
        .select("id, status")
        .eq("class", context.selectedClass)
        .eq("semester", context.semester)
        .eq("exam_type", context.examType)
        .eq("academic_year", context.academicYear)
        .in("status", ["draft", "pending_approval", "approved"])
        .limit(1);

      let batchId: string;

      if (existingBatch && existingBatch.length > 0) {
        const existing = existingBatch[0];
        if (existing.status !== "draft") {
          throw new Error("A batch for this context is pending approval or approved. Resolve it first.");
        }
        const { error: updateErr } = await supabase.from("exam_batches").update({
          total_students: matchedStudentCount,
          total_errors: parsed.totalErrors,
          uploaded_by: user.id,
        }).eq("id", existing.id);
        if (updateErr) throw updateErr;
        batchId = existing.id;

        // Delete old results to prevent duplicates on re-upload
        const { error: delErr } = await supabase
          .from("exam_results")
          .delete()
          .eq("batch_id", batchId);
        if (delErr) throw delErr;
      } else {
        const { data: batch, error: batchErr } = await supabase
          .from("exam_batches")
          .insert({
            class: context.selectedClass,
            semester: context.semester,
            exam_type: context.examType,
            academic_year: context.academicYear,
            uploaded_by: user.id,
            total_students: matchedStudentCount,
            total_errors: parsed.totalErrors,
            status: "draft",
          })
          .select("id")
          .single();
        if (batchErr) throw batchErr;
        batchId = batch.id;
      }

      // Set batch_id on all result rows
      for (const r of results) {
        r.batch_id = batchId;
      }

      // Insert in chunks of 500 to avoid timeout/payload limits
      const chunks = chunkArray(results, 500);
      for (let i = 0; i < chunks.length; i++) {
        const { error: resErr } = await supabase.from("exam_results").insert(chunks[i]);
        if (resErr) throw resErr;
      }

      return { imported: results.length, batchId };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["exam-batches"] });
      queryClient.invalidateQueries({ queryKey: ["exam-results"] });
      queryClient.invalidateQueries({ queryKey: ["exam-batch-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["my-verifications"] });
      queryClient.invalidateQueries({ queryKey: ["module-visibility"] });
      const d = data as any;
      toast({
        title: d.reupload ? "Re-upload applied" : "Results imported",
        description: d.reupload
          ? `${d.imported} module score(s) updated; GPA synced for ${d.gpaSynced ?? 0} student(s); ${d.overallChanged ?? 0} overall remark(s) refreshed; ${d.skipped} skipped. ${d.batchCount ?? 0} batch(es) reset to Draft — please submit for approval to restart the workflow.`
          : `${data.imported} results saved as draft`,
      });
      if (d.reupload) {
        setReuploadReport({
          imported: d.imported,
          overallChanged: d.overallChanged ?? 0,
          gpaSynced: d.gpaSynced ?? 0,
          batchCount: d.batchCount ?? 0,
          skippedDetails: d.skippedDetails ?? [],
        });
      }
      setParsed(null);
      setActiveTab("approval");
    },
    onError: (e: any) => {
      const message = String(e?.message ?? "Unknown import error");
      const isAccessError = message.toLowerCase().includes("row-level security") || message.toLowerCase().includes("permission denied");
      toast({
        title: "Import failed",
        description: isAccessError
          ? "Your account is not allowed to save this result upload. Please reload and try again; if it persists, verify the user has Head of ICT or Examination access."
          : message,
        variant: "destructive",
      });
    },
  });

  return (
    <div className="space-y-4">
      <ResultSetup context={context} onChange={setContext} />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid w-full ${tabsLayoutClassName}`}>
          {canManageExamination && (
            <TabsTrigger value="bulk" className="text-xs gap-1">
              <Upload className="h-3 w-3" /> Bulk Upload
            </TabsTrigger>
          )}
          {canManageExamination && (
            <TabsTrigger value="individual" className="text-xs gap-1">
              <UserPlus className="h-3 w-3" /> Individual
            </TabsTrigger>
          )}
          <TabsTrigger value="approval" className="text-xs gap-1">
            <ClipboardList className="h-3 w-3" /> Approval
          </TabsTrigger>
          {canManageExamination && (
            <TabsTrigger value="modules" className="text-xs gap-1">
              <BookOpen className="h-3 w-3" /> Modules
            </TabsTrigger>
          )}
          {canManageExamination && (
            <TabsTrigger value="template" className="text-xs gap-1">
              <FileSpreadsheet className="h-3 w-3" /> Template
            </TabsTrigger>
          )}
          {canSeeVisibility && (
            <TabsTrigger value="visibility" className="text-xs gap-1">
              <Shield className="h-3 w-3" /> Visibility
            </TabsTrigger>
          )}
        </TabsList>

        {canManageExamination && (
          <TabsContent value="bulk" className="space-y-4">
            <BulkUpload context={context} onParsed={setParsed} />
            {parsed && (
              <ValidationPreview
                parsed={parsed}
                onConfirm={() => saveBatchMutation.mutate()}
                onCancel={() => setParsed(null)}
                saving={saveBatchMutation.isPending}
              />
            )}
          </TabsContent>
        )}

        {canManageExamination && (
          <TabsContent value="individual">
            <IndividualEntry context={context} />
          </TabsContent>
        )}

        <TabsContent value="approval">
          <ApprovalWorkflow
            onReupload={(ctx) => {
              setContext((prev) => ({
                ...prev,
                selectedClass: ctx.selectedClass,
                semester: ctx.semester,
                examType: ctx.examType,
                academicYear: ctx.academicYear,
              }));
              setActiveTab(canManageExamination ? "bulk" : "approval");
            }}
          />
        </TabsContent>

        {canManageExamination && (
          <TabsContent value="modules">
            <ModulesManager />
          </TabsContent>
        )}

        {canManageExamination && (
          <TabsContent value="template">
            <TemplateGenerator context={context} />
          </TabsContent>
        )}

        {canSeeVisibility && (
          <TabsContent value="visibility" className="space-y-4">
            <ResultVisibilityManager />
            <StudentResultVisibilityBrowser />
          </TabsContent>
        )}
      </Tabs>

      <ReuploadReportDialog
        report={reuploadReport}
        onClose={() => setReuploadReport(null)}
      />
    </div>
  );
}

function ReuploadReportDialog({
  report,
  onClose,
}: {
  report: null | {
    imported: number;
    overallChanged: number;
    gpaSynced: number;
    batchCount: number;
    skippedDetails: Array<{ student: string; module: string; reason: string }>;
  };
  onClose: () => void;
}) {
  const { toast } = useToast();
  if (!report) return null;

  // Group skipped by student for a tidy display
  const byStudent = new Map<string, { reasons: Set<string>; modules: string[] }>();
  for (const s of report.skippedDetails) {
    const cur = byStudent.get(s.student) || { reasons: new Set<string>(), modules: [] };
    cur.reasons.add(s.reason);
    if (s.module && s.module !== "(all)") cur.modules.push(s.module);
    byStudent.set(s.student, cur);
  }

  const suspiciousFiv = (s: string) => !/^N?S\d{3,4}\/\d{3,4}\/\d{4}$/i.test(s);

  const copyList = async () => {
    const lines = Array.from(byStudent.entries()).map(([student, info]) => {
      const reasons = Array.from(info.reasons).join("; ");
      const mods = info.modules.length ? ` [modules: ${info.modules.join(", ")}]` : "";
      const flag = suspiciousFiv(student) ? " ⚠ tahajia ya Form IV inaonekana batili" : "";
      return `${student} — ${reasons}${mods}${flag}`;
    });
    try {
      await navigator.clipboard.writeText(lines.join("\n"));
      toast({ title: "Imenakiliwa", description: `${lines.length} mwanafunzi waliorukwa wamenakiliwa kwenye clipboard.` });
    } catch {
      toast({ title: "Nakili imeshindikana", variant: "destructive" });
    }
  };

  return (
    <Dialog open={!!report} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Ripoti ya Re-upload</DialogTitle>
          <DialogDescription>
            Muhtasari wa matokeo yaliyosasishwa na wanafunzi waliorukwa.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="rounded-md border p-3">
              <div className="text-xs text-muted-foreground">Modules updated</div>
              <div className="text-lg font-semibold">{report.imported}</div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs text-muted-foreground">GPA synced</div>
              <div className="text-lg font-semibold">{report.gpaSynced}</div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs text-muted-foreground">Overall remarks</div>
              <div className="text-lg font-semibold">{report.overallChanged}</div>
            </div>
            <div className="rounded-md border p-3">
              <div className="text-xs text-muted-foreground">Batches reset</div>
              <div className="text-lg font-semibold">{report.batchCount}</div>
            </div>
          </div>

          {byStudent.size > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="font-medium">Waliorukwa ({byStudent.size})</div>
                <Button size="sm" variant="outline" onClick={copyList}>Copy list</Button>
              </div>
              <div className="max-h-72 overflow-auto rounded-md border">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-2">Form IV / Student</th>
                      <th className="text-left p-2">Sababu</th>
                      <th className="text-left p-2">Moduli</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from(byStudent.entries()).map(([student, info]) => (
                      <tr key={student} className="border-t">
                        <td className="p-2 font-mono">
                          {student}
                          {suspiciousFiv(student) && (
                            <span className="ml-2 text-amber-600">⚠ tahajia batili</span>
                          )}
                        </td>
                        <td className="p-2">{Array.from(info.reasons).join("; ")}</td>
                        <td className="p-2">{info.modules.length ? info.modules.join(", ") : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground">
                Sababu za kawaida: <strong>no prior result</strong> = mwanafunzi hayuko kwenye batch ya kwanza (fanya fresh upload); <strong>already passed</strong> = moduli hiyo ilikwisha pita; <strong>student not found in any prior failing batch</strong> = jina/Form IV haifanani au hakuwemo. Tahajia kama <code>Np</code> badala ya <code>Ns</code> ni ya kawaida.
              </p>
            </div>
          ) : (
            <p className="text-xs text-muted-foreground">Hakuna mwanafunzi aliyerukwa. 🎉</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
